import { mutators } from "@oblique/shared/zero"
import { useZero } from "@rocicorp/zero/react"
import {
	type EdgeProps,
	getSmoothStepPath,
	type Handle,
	useReactFlow,
	useStore,
} from "@xyflow/react"
import { useParams } from "react-router"

type HandleMeta = {
	kind: "socket" | "pill"
	x: number
	y: number
	width: number
	height: number
	centerX: number
	centerY: number
	maskCircle?: {
		cx: number
		cy: number
		r: number
	}
}

type PillMaskMeta = {
	x: number
	y: number
	width: number
	height: number
}

type NodeLookupEntry = {
	internals: {
		positionAbsolute: { x: number; y: number }
		handleBounds?: {
			source: Handle[] | null
			target: Handle[] | null
		}
	}
}

function escapeSelector(value: string) {
	if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
		return CSS.escape(value)
	}

	return value.replace(/["\\]/g, "\\$&")
}

function getRectMeta(
	rect: DOMRect,
	screenToFlowPosition: (position: { x: number; y: number }) => {
		x: number
		y: number
	},
) {
	const topLeft = screenToFlowPosition({ x: rect.left, y: rect.top })
	const bottomRight = screenToFlowPosition({ x: rect.right, y: rect.bottom })

	return {
		x: topLeft.x,
		y: topLeft.y,
		width: bottomRight.x - topLeft.x,
		height: bottomRight.y - topLeft.y,
		centerX: (topLeft.x + bottomRight.x) / 2,
		centerY: (topLeft.y + bottomRight.y) / 2,
	}
}

function getHandleMeta({
	nodeId,
	handleId,
	type,
	nodeLookup,
	flowDomNode,
	screenToFlowPosition,
}: {
	nodeId: string
	handleId?: string | null
	type: "source" | "target"
	nodeLookup: Map<string, NodeLookupEntry>
	flowDomNode: HTMLDivElement | null
	screenToFlowPosition: (position: { x: number; y: number }) => {
		x: number
		y: number
	}
}): HandleMeta | null {
	const node = nodeLookup.get(nodeId)
	const handles = node?.internals.handleBounds?.[type]
	if (!node || !handles) return null

	const handle =
		(handleId
			? handles.find((candidate) => candidate.id === handleId)
			: handles[0]) ?? null
	if (!handle) return null

	const baseSelector = handle.id
		? `.react-flow__handle.${type}[data-nodeid="${escapeSelector(nodeId)}"][data-handleid="${escapeSelector(handle.id)}"]`
		: `.react-flow__handle.${type}[data-nodeid="${escapeSelector(nodeId)}"]`
	const handleElement =
		flowDomNode?.querySelector<HTMLElement>(baseSelector) ?? null
	const kind =
		(handleElement?.dataset.obliqueHandleKind as
			| "socket"
			| "pill"
			| undefined) ?? (handle.width > handle.height ? "pill" : "socket")

	const x = node.internals.positionAbsolute.x + handle.x
	const y = node.internals.positionAbsolute.y + handle.y
	const stateDotElement =
		kind === "socket"
			? (handleElement?.parentElement?.querySelector<HTMLElement>(
					"[data-oblique-state-dot]",
				) ?? null)
			: null
	const stateDotMeta =
		stateDotElement && flowDomNode
			? getRectMeta(
					stateDotElement.getBoundingClientRect(),
					screenToFlowPosition,
				)
			: null

	return {
		kind,
		x,
		y,
		width: handle.width,
		height: handle.height,
		centerX: x + handle.width / 2,
		centerY: y + handle.height / 2,
		maskCircle: stateDotMeta
			? {
					cx: stateDotMeta.centerX,
					cy: stateDotMeta.centerY,
					r: Math.min(stateDotMeta.width, stateDotMeta.height) / 2,
				}
			: undefined,
	}
}

function getSocketMaskRadius(meta: HandleMeta) {
	const outerRadius = Math.min(meta.width, meta.height) / 2

	return Math.max(8, outerRadius - 3)
}

function getMaskBounds(
	sourceX: number,
	sourceY: number,
	targetX: number,
	targetY: number,
) {
	const minX = Math.min(sourceX, targetX) - 80
	const minY = Math.min(sourceY, targetY) - 80

	return {
		x: minX,
		y: minY,
		width: Math.abs(sourceX - targetX) + 160,
		height: Math.abs(sourceY - targetY) + 160,
	}
}

function intersectsBounds(
	rect: { x: number; y: number; width: number; height: number },
	bounds: { x: number; y: number; width: number; height: number },
) {
	return (
		rect.x < bounds.x + bounds.width &&
		rect.x + rect.width > bounds.x &&
		rect.y < bounds.y + bounds.height &&
		rect.y + rect.height > bounds.y
	)
}

function getPillMasks({
	flowDomNode,
	screenToFlowPosition,
	maskBounds,
}: {
	flowDomNode: HTMLDivElement | null
	screenToFlowPosition: (position: { x: number; y: number }) => {
		x: number
		y: number
	}
	maskBounds: { x: number; y: number; width: number; height: number }
}): PillMaskMeta[] {
	if (!flowDomNode) return []

	return Array.from(
		flowDomNode.querySelectorAll<HTMLElement>("[data-oblique-pill]"),
	)
		.filter((element) => element.dataset.obliquePillTone !== "socket")
		.map((element) =>
			getRectMeta(element.getBoundingClientRect(), screenToFlowPosition),
		)
		.filter((rect) => intersectsBounds(rect, maskBounds))
}

export function NodeEdge({
	id,
	source,
	target,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	style = {},
	markerEnd,
	selected,
}: EdgeProps) {
	const z = useZero()
	const { projectId } = useParams<{ projectId: string }>()
	const reactFlow = useReactFlow()
	const flowDomNode = useStore((state) => state.domNode)
	const nodeLookup = useStore(
		(state) => state.nodeLookup as unknown as Map<string, NodeLookupEntry>,
	)
	const edgeRecord = useStore((state) =>
		state.edges.find((edge) => edge.id === id),
	)

	const sourceMeta = getHandleMeta({
		nodeId: source,
		handleId: edgeRecord?.sourceHandle,
		type: "source",
		nodeLookup,
		flowDomNode,
		screenToFlowPosition: reactFlow.screenToFlowPosition,
	})
	const targetMeta = getHandleMeta({
		nodeId: target,
		handleId: edgeRecord?.targetHandle,
		type: "target",
		nodeLookup,
		flowDomNode,
		screenToFlowPosition: reactFlow.screenToFlowPosition,
	})

	const adjustedSourceX = sourceMeta?.centerX ?? sourceX
	const adjustedSourceY = sourceMeta?.centerY ?? sourceY
	const adjustedTargetX = targetMeta?.centerX ?? targetX
	const adjustedTargetY = targetMeta?.centerY ?? targetY
	const maskBounds = getMaskBounds(
		adjustedSourceX,
		adjustedSourceY,
		adjustedTargetX,
		adjustedTargetY,
	)
	const pillMasks = getPillMasks({
		flowDomNode,
		screenToFlowPosition: reactFlow.screenToFlowPosition,
		maskBounds,
	})
	const maskId = `oblique-edge-mask-${id.replace(/[^a-zA-Z0-9_-]/g, "-")}`

	const [edgePath] = getSmoothStepPath({
		sourceX: adjustedSourceX,
		sourceY: adjustedSourceY,
		targetX: adjustedTargetX,
		targetY: adjustedTargetY,
		sourcePosition,
		targetPosition,
		borderRadius: 20,
	})

	const midX = (adjustedSourceX + adjustedTargetX) / 2
	const midY = (adjustedSourceY + adjustedTargetY) / 2

	return (
		<>
			<defs>
				<mask
					id={maskId}
					maskUnits="userSpaceOnUse"
					x={maskBounds.x}
					y={maskBounds.y}
					width={maskBounds.width}
					height={maskBounds.height}
				>
					<rect
						x={maskBounds.x}
						y={maskBounds.y}
						width={maskBounds.width}
						height={maskBounds.height}
						fill="white"
					/>
					{sourceMeta?.kind === "pill" ? (
						<rect
							x={sourceMeta.x}
							y={sourceMeta.y}
							width={sourceMeta.width}
							height={sourceMeta.height}
							rx={20}
							ry={20}
							fill="black"
						/>
					) : null}
					{targetMeta?.kind === "pill" ? (
						<rect
							x={targetMeta.x}
							y={targetMeta.y}
							width={targetMeta.width}
							height={targetMeta.height}
							rx={20}
							ry={20}
							fill="black"
						/>
					) : null}
					{pillMasks.map((pillMask, index) => (
						<rect
							key={`pill-mask-${index}`}
							x={pillMask.x}
							y={pillMask.y}
							width={pillMask.width}
							height={pillMask.height}
							rx={20}
							ry={20}
							fill="black"
						/>
					))}
					{sourceMeta?.kind === "socket" ? (
						<circle
							cx={sourceMeta.maskCircle?.cx ?? sourceMeta.centerX}
							cy={sourceMeta.maskCircle?.cy ?? sourceMeta.centerY}
							r={sourceMeta.maskCircle?.r ?? getSocketMaskRadius(sourceMeta)}
							fill="black"
						/>
					) : null}
					{targetMeta?.kind === "socket" ? (
						<circle
							cx={targetMeta.maskCircle?.cx ?? targetMeta.centerX}
							cy={targetMeta.maskCircle?.cy ?? targetMeta.centerY}
							r={targetMeta.maskCircle?.r ?? getSocketMaskRadius(targetMeta)}
							fill="black"
						/>
					) : null}
				</mask>
			</defs>
			{selected ? (
				<path
					d={edgePath}
					stroke="var(--node-selection-glow)"
					fill="none"
					strokeLinecap="round"
					mask={`url(#${maskId})`}
					style={{ strokeWidth: 8, opacity: 0.18 }}
				/>
			) : null}
			<path
				id={id}
				className="react-flow__edge-path"
				d={edgePath}
				stroke={selected ? "var(--node-selection)" : "var(--node-connection)"}
				fill="none"
				markerEnd={markerEnd}
				strokeLinecap="round"
				mask={`url(#${maskId})`}
				style={{
					...style,
					strokeWidth: 5,
					transition: "stroke 0.12s ease, stroke-width 0.12s ease",
					opacity: selected ? 1 : 0.92,
				}}
			/>
			<path
				d={edgePath}
				stroke="transparent"
				fill="none"
				style={{ cursor: "pointer", strokeWidth: 24 }}
			/>
			{selected && (
				<foreignObject
					x={midX - 9}
					y={midY - 9}
					width={18}
					height={18}
					requiredExtensions="http://www.w3.org/1999/xhtml"
				>
					<div className="flex h-[18px] w-[18px] cursor-pointer items-center justify-center">
						<button
							type="button"
							onClick={(event) => {
								event.stopPropagation()
								if (!projectId) return
								z.mutate(
									mutators.edges.delete({
										project_id: projectId,
										edge_id: id,
									}),
								)
							}}
							className="flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-full border-2 border-white bg-[var(--node-selection)] shadow-[0_0_0_1px_rgba(134,0,237,0.3)] transition-transform hover:scale-105"
							aria-label="Delete connection"
						>
							<span className="h-2 w-2 rounded-full border border-white/80 bg-[var(--node-selection)]" />
						</button>
					</div>
				</foreignObject>
			)}
		</>
	)
}
